/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel, PublicClientApplication } from '@azure/msal-browser'
import { setMsalInstance, updateAuthority } from './store/msalInstance/msalInstanceSlice'
import { store } from './store/store'

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
	names: {
		signUpSignIn: 'B2C_1_SignInExternals',
	},
	authorities: {
		signUpSignIn: {
			authority: process.env.REACT_APP_B2C_SIGNUP_SIGNIN_AUTHORITY,
		},
		signInExternals: {
			authority: process.env.REACT_APP_B2C_SIGNIN_EXTERNALS_AUTHORITY,
		},
		signInInternals: {
			authority: process.env.REACT_APP_B2C_SIGNIN_INTERNALS_AUTHORITY,
		},
		forgotPassword: {
			authority: process.env.REACT_APP_B2C_FORGOT_PASSWORD,
		},
	},
	authorityDomain: process.env.REACT_APP_B2C_AUTHORITY_DOMAIN,
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
	auth: {
		clientId: process.env.REACT_APP_B2C_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
		authority: b2cPolicies.authorities.signInInternals.authority,
		knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
		redirectUri: '/', // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
		postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
		navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
	},
	cache: {
		cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
		storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
	},
	system: {
		loggerOptions: {
			loggerCallback: (level, message, containsPii) => {
				if (containsPii) {
					return
				}
				switch (level) {
					case LogLevel.Error:
						console.error(message)
						return
					case LogLevel.Info:
						console.info(message)
						return
					case LogLevel.Verbose:
						console.debug(message)
						return
					case LogLevel.Warning:
						console.warn(message)
						return
				}
			},
		},
	},
}

export const msalInstance = () => {
	const state = store.getState()
	if (Object.keys(state.msalInstance.msalInstance).length === 0) {
		const instance = new PublicClientApplication(msalConfig)
		instance.handleRedirectPromise().catch((e) =>
			console.error('MSAL handleRedirectPromise error:', e)
		)
		store.dispatch(setMsalInstance(instance))
		return instance
	}
	return state.msalInstance.msalInstance
}

export const setAuthority = (isInternal) => {
	if (isInternal) {
		store.dispatch(updateAuthority(b2cPolicies.authorities.signInInternals.authority))
	} else {
		store.dispatch(updateAuthority(b2cPolicies.authorities.signInExternals.authority))
	}
}

store.subscribe(msalInstance)

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
	scopes: [],
}

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
	scopes: ['openid', 'profile', process.env.REACT_APP_B2C_SILENT_REQUEST],
}

export const fetchActiveAccount = () => msalInstance().getActiveAccount()

export const FetchToken = async () => {
	let account = fetchActiveAccount()

	if (!account) {
		const accounts = msalInstance().getAllAccounts()
		if (accounts.length > 0) {
			msalInstance().setActiveAccount(accounts[0])
			account = accounts[0]
		}
	}

	if (!account) {
		throw new Error('no_active_account')
	}
	const tokenRequest = {
		account: account,
		scopes: silentRequest.scopes,
	}
	let response

	try {
		response = await msalInstance().acquireTokenSilent(tokenRequest)
	} catch (e) {
		throw e
	}
	return response.accessToken
}

export const setActiveAccount = () => {
	const accounts = msalInstance().getAllAccounts()

	if (accounts.length > 0) {
		msalInstance().setActiveAccount(accounts[0])
	}
}
